import React, { useCallback, useMemo, useState } from 'react';
import { formatRoute } from 'react-router-named-routes';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import Icon from '@mdi/react';
import { useNavigate } from 'react-router-dom';
import { mdiCalendarMonth, mdiSwapHorizontalCircle } from '@mdi/js';
import { useDispatch, useSelector } from 'react-redux';
import { MovableCardWrapper } from '../common/board/styles/Base';
import DeleteCardConfirmPopover from '../common/delete_card_confirm_popover';
import { usePlannerViewWeekStartDate } from './planner_hooks';
import imgPlaceholder from '../../assets/images/imgPlaceholder.png';
import imgPlaceholderLoggedMeal from '../../assets/images/imgPlaceholderLoggedMeal.png';
import {
  scaleMeal,
  updateFoodBrainDerivedDataOnMeal,
} from '../../operations/meal_operations';
import MealTypesChips from '../meal/meal_types_chips';
import Nutrition from '../meal/nutrition';
import StepInput from '../common/step_input';
import { useIsMobile } from '../common/layout_hooks';
import EndUserRescheduleMealModal from '../meal/end_user_reschedule_meal_modal';
import { END_USER_FIND_ALTERNATIVES_ROUTE } from '../../services/routes';
import { linkWithQueryParams } from '../../services/urls';
import {
  useCreateMealMutation,
  useDeleteMealMutation,
  useGetDbPlannerWeekQuery,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useLazyGetFullMealQuery,
  useUpdateDbPlannerWeekOptimisticallyMutation,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import {
  mealsAddedToMealBasket,
  mealsRemovedFromMealBasket,
} from '../../features/end_user_shopping/end_user_shopping_slice';
import {
  currentViewWeekStartDateStr,
  daysArrayIndex,
  dbWeekStartDateForViewWeekAndIndex,
  dbWeekStartDatesCoveringViewWeek,
  findPlannerEntry,
  isLoggedMeal,
  plannerEntriesIndexOfObject,
  plannerWeekWithMealAdded,
} from '../../services/planner';
import { mergeMyDayActionRecords } from '../../services/programmes';
import { newMealWithDefaultFields } from '../../services/meals';
import { cloneObject } from '../../operations/utils';
import { trackEvents, useTracker } from '../../operations/tracking_operations';
import {
  copyMealToPlanner,
  getOrCreatePlannerWeeks,
  moveMealInPlanner,
} from '../../operations/planner_operations';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetDistilledMealByIDAndParentQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { isMealBackgroundProcessingSelector } from '../../features/recipes/recipes_slice';
import { MealBasketButton } from '../../features/end_user_shopping/components/meal_basket_button';

const PlannerEntryMealCard = ({
  id,
  title,
  style,
  className,
  onClick,
  onDelete,
}) => {
  const dayIndex = style._dayIndex;
  const parentID = style._parentID;

  const { data: userProfile } = useGetUserProfileQuery();

  const tracker = useTracker();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const { data: myDayActionRecordsByEnrollment } =
    useGetMyDayActionRecordsByEnrollmentQuery(currentProgrammeEnrollment?.id, {
      skip: !currentProgrammeEnrollment?.id,
    });

  const { data: myDayActionRecordsBySpaceMembership } =
    useGetMyDayActionRecordsBySpaceMembershipQuery(currentSpaceMembership?.id, {
      skip: !currentSpaceMembership?.id,
    });

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const plannerViewWeekStartDate =
    usePlannerViewWeekStartDate() ||
    currentViewWeekStartDateStr(
      1 /* plannerViewWeekStartDayOrDefault(userProfile?.plannerViewWeekStartDay) */,
    );

  const isMobile = useIsMobile();

  const isBackgroundProcessing = useSelector((state) =>
    isMealBackgroundProcessingSelector(state, id),
  );

  // const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: distilledMeal } = useGetDistilledMealByIDAndParentQuery(
    { id, parentID },
    { skip: !id || !parentID },
  );

  const plannerDbWeekStartDate = dbWeekStartDateForViewWeekAndIndex(
    plannerViewWeekStartDate,
    dayIndex,
  );

  const { data: dbPlannerWeek } = useGetDbPlannerWeekQuery(
    plannerDbWeekStartDate,
  );

  const [deleteMeal] = useDeleteMealMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekOptimisticallyMutation();

  // console.log({ meal, id, parentID });

  const servings = distilledMeal?.recipes[0]?.servings;
  const isInMealBasket = useSelector((state) =>
    state.endUserShopping.mealBasketItems.some((item) => item.meal.id === id),
  );
  const calories =
    distilledMeal?.derivedNutrition?.totalNutritionPerServing?.calories;
  const mealTypes = useMemo(
    () => distilledMeal?.recipes[0] && distilledMeal?.recipes[0].mealTypes,
    [distilledMeal?.recipes],
  );

  const [deleteMealAnchorEl, setDeleteMealAnchorEl] = useState(null);

  const [addToPlannerAnchorEl, setAddToPlannerAnchorEl] = useState(null);

  const [scalingInProgress, setScalingInProgress] = useState(false);

  const setGlobalSnackbar = useCallback(
    (obj) => dispatch(showGlobalSnackbar(obj)),
    [dispatch],
  );

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const onClickCopyToPlanner = useCallback(
    async (toPlannerViewWeekStartDate, toDayIndexes) => {
      const { data: fullMeal } = await lazyGetMeal(id);
      copyMealToPlanner(
        fullMeal,
        toPlannerViewWeekStartDate,
        toDayIndexes,
        customerFoodDataID,
        tracker,
        userProfile,
        currentSpaceMembership,
        lazyGetOrCreateDbPlannerWeekByStartDate,
        createMeal,
        updateMeal,
        updateDbPlannerWeek,
        lazyGetMeal,
      );
      setAddToPlannerAnchorEl(null);
      const snackbarParams = {
        notificationText: 'Your meal has been copied',
      };
      if (plannerViewWeekStartDate !== toPlannerViewWeekStartDate) {
        snackbarParams.linkText = 'View planner';
        snackbarParams.linkTarget = `/planner/${toPlannerViewWeekStartDate}`;
      }
      setGlobalSnackbar(snackbarParams);
    },
    [
      createMeal,
      currentSpaceMembership,
      customerFoodDataID,
      id,
      lazyGetOrCreateDbPlannerWeekByStartDate,
      lazyGetMeal,
      plannerViewWeekStartDate,
      setGlobalSnackbar,
      tracker,
      updateDbPlannerWeek,
      updateMeal,
      userProfile,
    ],
  );

  const onClickMove = useCallback(
    async (toPlannerViewWeekStartDate, toDayIndexes) => {
      dispatch(mealsRemovedFromMealBasket([id]));
      const { data: fullMeal } = await lazyGetMeal(id);
      return moveMealInPlanner(
        fullMeal,
        dbPlannerWeek,
        dayIndex,
        toPlannerViewWeekStartDate,
        toDayIndexes,
        customerFoodDataID,
        tracker,
        userProfile,
        currentSpaceMembership,
        lazyGetOrCreateDbPlannerWeekByStartDate,
        createMeal,
        updateMeal,
        deleteMeal,
        updateDbPlannerWeek,
        lazyGetMeal,
      ).then(() => {
        setAddToPlannerAnchorEl(null);
        const snackbarParams = {
          notificationText: 'Your meal has been rescheduled',
        };
        if (plannerViewWeekStartDate !== toPlannerViewWeekStartDate) {
          snackbarParams.linkText = 'View planner';
          snackbarParams.linkTarget = `/planner/${toPlannerViewWeekStartDate}`;
        }
        setGlobalSnackbar(snackbarParams);
      });
    },
    [
      createMeal,
      currentSpaceMembership,
      customerFoodDataID,
      dayIndex,
      dbPlannerWeek,
      deleteMeal,
      dispatch,
      id,
      lazyGetOrCreateDbPlannerWeekByStartDate,
      lazyGetMeal,
      plannerViewWeekStartDate,
      setGlobalSnackbar,
      tracker,
      updateDbPlannerWeek,
      updateMeal,
      userProfile,
    ],
  );

  const onStepInputChange = useCallback(
    async (ev) => {
      // ev.preventDefault();
      console.log(ev.target.value);
      const scaleToServings = ev.target.value;
      const scaleToServingsNumber = Number(scaleToServings) || 1;
      if (scaleToServingsNumber !== servings) {
        const scaleFactor = scaleToServingsNumber / (servings || 1);
        setScalingInProgress(true);
        try {
          const { data: fullMeal } = await lazyGetMeal(id);
          return scaleMeal(
            fullMeal,
            scaleFactor,
            scaleToServingsNumber,
            customerFoodDataID,
            false,
            updateMeal,
            dispatch,
            lazyGetMeal,
          );
        } finally {
          setScalingInProgress(false);
        }
      }
      return Promise.resolve();
    },
    [servings, lazyGetMeal, id, customerFoodDataID, updateMeal, dispatch],
  );

  const onCardClick = useCallback(() => onClick(id), [id, onClick]);

  const onClickReschedule = useCallback((ev) => {
    setAddToPlannerAnchorEl(ev.target);
    ev.stopPropagation();
  }, []);

  const dbWeekStartDates = dbWeekStartDatesCoveringViewWeek(
    plannerViewWeekStartDate,
  );

  const currentHealthProGroup = null;

  const onClickQuickDuplicate = useCallback(
    async (ev) => {
      ev.stopPropagation();
      const plannerWeeks = await getOrCreatePlannerWeeks(
        dbWeekStartDates,
        lazyGetOrCreateDbPlannerWeekByStartDate,
      );
      const { plannerWeek } = findPlannerEntry(
        plannerWeeks,
        dbWeekStartDates,
        id,
      );
      const currentPosition = plannerEntriesIndexOfObject(
        plannerWeek.days[daysArrayIndex(dayIndex)].entries,
        id,
      );
      console.log({ objectID: id, dayIndex, currentPosition });
      const { data: fullMeal } = await lazyGetMeal(id);
      const { data: insertedMeal } = await createMeal(
        newMealWithDefaultFields(
          cloneObject(fullMeal),
          plannerWeek.id,
          currentSpaceMembership.id,
          currentHealthProGroup,
        ),
      );
      const updatedPlannerWeek = plannerWeekWithMealAdded(
        plannerWeek,
        insertedMeal,
        dayIndex,
        currentPosition + 1,
      );

      await updateDbPlannerWeek(updatedPlannerWeek);

      tracker(userProfile, ['Add to planner'], ['numMealsPlanned']);
      await updateFoodBrainDerivedDataOnMeal(
        insertedMeal,
        customerFoodDataID,
        updateMeal,
      );
    },
    [
      createMeal,
      currentSpaceMembership.id,
      customerFoodDataID,
      dayIndex,
      dbWeekStartDates,
      id,
      lazyGetOrCreateDbPlannerWeekByStartDate,
      lazyGetMeal,
      tracker,
      updateDbPlannerWeek,
      updateMeal,
      userProfile,
    ],
  );

  const onClickFindAlternatives = useCallback(
    (ev) => {
      ev.stopPropagation();
      navigate(
        linkWithQueryParams(
          formatRoute(END_USER_FIND_ALTERNATIVES_ROUTE, {
            calendarDay: style._calendarDay,
          }),
          {
            mealType: style._mealType,
            mealId: id,
            scrollCookie: new Date().getTime().toString(),
          },
        ),
      );
    },
    [id, navigate, style._calendarDay, style._mealType],
  );

  const onClickRemoveFromMealBasket = useCallback(
    (ev) => {
      dispatch(mealsRemovedFromMealBasket([id]));
      ev.stopPropagation();
    },
    [dispatch, id],
  );

  const onClickAddToMealBasket = useCallback(
    async (ev) => {
      trackEvents([
        { name: 'Meals added to basket', args: { source: 'planner' } },
      ]);
      const { data: fullMeal } = await lazyGetMeal(id);
      dispatch(mealsAddedToMealBasket([fullMeal]));
      ev.stopPropagation();
    },
    [dispatch, id, lazyGetMeal],
  );

  const onClickDelete = useCallback((ev) => {
    setDeleteMealAnchorEl(ev.target);
    ev.stopPropagation();
  }, []);

  const onConfirmDelete = useCallback(() => onDelete(id), [id, onDelete]);

  const onDismissDeletePopover = useCallback(
    () => setDeleteMealAnchorEl(null),
    [],
  );

  const onDismissAddToPlannerModal = useCallback(
    () => setAddToPlannerAnchorEl(null),
    [],
  );

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const mealIsLogged = isLoggedMeal(myDayActionRecords, id);

  if (!distilledMeal) {
    return null;
  }

  const quickDuplicateEnabled = !isMobile;

  const imgPlaceholderToUse = mealIsLogged
    ? imgPlaceholderLoggedMeal
    : imgPlaceholder;

  return (
    <MovableCardWrapper
      data-id={id}
      onClick={onCardClick}
      style={style}
      className={className}>
      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex' }}>
          <CardMedia
            component="img"
            className="plannerCardImage"
            image={distilledMeal.recipes[0].imageUrl || imgPlaceholderToUse}
            title={title}
          />
          <CardContent
            title={title}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderBottom: '1px solid #7B7B7B',
            }}>
            <Typography variant="body2" component="p" sx={{ flexGrow: 1 }}>
              {title}
            </Typography>
            <Grid container>
              <Grid item xs={6} className="mealCardNutrition">
                {calories && (
                  <Nutrition metric="calories" quantity={calories} />
                )}
              </Grid>
              <Grid item xs={6} style={{ display: 'flex' }}>
                {isBackgroundProcessing && (
                  <CircularProgress size={20} style={{ margin: 'auto' }} />
                )}
                {!isBackgroundProcessing && (
                  <div
                    className="mealCardServingsStepInput"
                    style={{ margin: 'auto' }}>
                    <StepInput
                      value={servings}
                      onChange={onStepInputChange}
                      isLoading={scalingInProgress}
                    />
                  </div>
                )}
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex' }}>
              <MealTypesChips mealTypes={mealTypes} />
            </Box>
          </CardContent>
        </Box>
        <CardActions sx={{ justifyContent: 'space-around' }}>
          <Tooltip title="Reschedule" placement="top-start">
            <IconButton
              aria-label="reschedule"
              size="small"
              onClick={onClickReschedule}>
              <Icon
                path={mdiCalendarMonth}
                style={{ width: '24px', height: '24px' }}
              />
            </IconButton>
          </Tooltip>
          {!mealIsLogged && quickDuplicateEnabled && (
            <Tooltip title="Duplicate this meal" placement="top-start">
              <IconButton
                aria-label="quick duplicate"
                size="small"
                onClick={onClickQuickDuplicate}>
                <FileCopyIcon />
              </IconButton>
            </Tooltip>
          )}
          {!mealIsLogged && (
            <Tooltip title="Find alternatives" placement="top-start">
              <IconButton
                aria-label="find alternatives"
                size="small"
                onClick={onClickFindAlternatives}>
                <Icon
                  path={mdiSwapHorizontalCircle}
                  style={{ width: '24px', height: '24px' }}
                />
              </IconButton>
            </Tooltip>
          )}
          {!mealIsLogged && (
            <MealBasketButton
              isInMealBasket={isInMealBasket}
              isAddingMealsToBasket={false}
              onAddToMealBasket={onClickAddToMealBasket}
              onRemoveFromMealBasket={onClickRemoveFromMealBasket}
            />
          )}
          <IconButton aria-label="delete" size="small" onClick={onClickDelete}>
            <DeleteIcon />
          </IconButton>
          <DeleteCardConfirmPopover
            visible={!!deleteMealAnchorEl}
            anchorEl={deleteMealAnchorEl}
            title="Delete meal"
            message="The meal will be deleted permanently. Are you sure?"
            height={140}
            onConfirmDelete={onConfirmDelete}
            onDismiss={onDismissDeletePopover}
          />
        </CardActions>
      </Card>
      {addToPlannerAnchorEl && (
        <EndUserRescheduleMealModal
          anchorEl={addToPlannerAnchorEl}
          onCopyToPlanner={onClickCopyToPlanner}
          disableMove={mealIsLogged}
          onMove={onClickMove}
          onDismiss={onDismissAddToPlannerModal}
        />
      )}
    </MovableCardWrapper>
  );
};

PlannerEntryMealCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default PlannerEntryMealCard;
